import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { apiEndpoints } from "../utils/constants";
import {
  getAccessToken,
  getRefreshToken,
  logout,
  setAccessToken,
} from "./accessToken";

/**
 * Single shared axios instance.
 *
 * Responsibilities:
 *   - attach the appropriate bearer token (access vs refresh) per request
 *   - silently retry once on 401/422/500 by refreshing the access token
 *   - force-logout on 444 (server-issued session kill)
 *   - never throw past application code without logging in dev
 *
 * Notes:
 *   - All `_retry` bookkeeping is on the request config so a retry storm
 *     can't happen.
 *   - Refresh failures are funneled into `logout()` so the UI can't get stuck
 *     in a half-authenticated state.
 */

const myaxios = axios.create();

type RetryableConfig = AxiosRequestConfig & { _retry?: boolean };

// ---------- Request: attach the right token ----------
myaxios.interceptors.request.use(
  (config) => {
    try {
      const accessToken = getAccessToken();
      if (config.url === apiEndpoints.refresh) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${getRefreshToken() ?? ""}`,
        };
      } else if (accessToken) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${accessToken}`,
        };
      }
    } catch (err) {
      // If localStorage is unavailable (SSR / private mode), just send the
      // request unauthenticated — never block on it.
      if (process.env.NODE_ENV !== "production") {
         
        console.warn("[myaxios] request interceptor failed:", err);
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ---------- Response: refresh-on-expiry, friendly failure ----------
myaxios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = (error.config || {}) as RetryableConfig;
    const status = error?.response?.status;

    // Server hard-killed the session.
    if (status === 444) {
      try {
        logout();
      } catch {
        /* logout is best-effort */
      }
      return Promise.reject(error);
    }

    const shouldRefresh =
      (status === 401 || status === 422 || status === 500) &&
      originalRequest.url !== apiEndpoints.refresh &&
      !originalRequest._retry;

    if (shouldRefresh) {
      originalRequest._retry = true;
      try {
        const { status: refreshStatus, data } = await myaxios.get(
          apiEndpoints.refresh,
        );
        if (refreshStatus === 200 && data?.data?.accessToken) {
          setAccessToken(data.data.accessToken);
          myaxios.defaults.headers.common["Authorization"] =
            `Bearer ${data.data.accessToken}`;
          originalRequest.headers = {
            ...(originalRequest.headers || {}),
            Authorization: `Bearer ${data.data.accessToken}`,
          };
          return myaxios(originalRequest);
        }
      } catch (refreshError) {
        // Refresh itself failed → the user is effectively logged out.
        if (process.env.NODE_ENV !== "production") {
           
          console.warn("[myaxios] token refresh failed:", refreshError);
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export default myaxios;
